<template>
  <Modal
    v-model:show="isOpen"
    :show-close="false"
    :close-modal-bottom="true"
    body-classes="cpa-details__body"
    modal-classes="cpa-details__dialog"
  >
    <template v-slot:header>
      <div class="ui-d-flex ui-jc-between ui-ai-center w-100">
        <div class="cpa-conversions__title tw-mb-16">
          {{ $t('cpaConversions.details.title') }}
        </div>
      </div>
    </template>

    <div
      v-for="(curCpa, i) in states"
      :key="i"
      class="cpa-details"
    >
      <div
        v-if="showRootSection(curCpa)"
        class="cpa-details__title"
      >
        CPA
      </div>
      <div
        v-if="showRootSection(curCpa)"
        class="cpa-details__section cpa-details__section--root"
      >
        <div class="cpa-details__table">
          <div
            v-if="curCpa.amount !== null && curCpa.amount !== undefined"
            class="cpa-details__row"
          >
            <span>{{ $t('offers.cardOffers.payout') }}</span>
            <span>
              {{ getCurrencyLabel(curCpa.currency_code) }}{{ formatters.formatNumber(curCpa.amount) }}
            </span>
          </div>
          <div
            v-if="showCapPlayersRow(curCpa)"
            class="cpa-details__row"
          >
            <span>{{ $t('offers.cardOffers.cap_players') }}</span>
            <template v-if="curCpa.cap_players_count === 0">
              0
            </template>
            <template v-else>
              {{ getCapPlayersText(curCpa) }}
            </template>
          </div>
          <div
            v-if="curCpa.hold_seconds !== null && curCpa.hold_seconds !== undefined"
            class="cpa-details__row"
          >
            <span>{{ $t('offers.cardOffers.hold') }}</span>
            <span>{{ formatters.formatDurationDays(curCpa.hold_seconds) }}</span>
          </div>
        </div>
      </div>

      <template
        v-for="section in getSections(curCpa)"
        :key="section.title"
      >
        <div
          v-if="section.visible"
          class="cpa-details__section"
        >
          <div class="cpa-details__title cpa-details__title--facts">
            <span>{{ section.title }}</span>
            <span>{{ $t('cpaConversions.details.req') }}</span>
            <span>{{ $t('cpaConversions.details.fact') }}</span>
          </div>
          <div class="cpa-details__table cpa-details__table--facts">
            <div
              v-for="(rowItem, idx) in section.entries"
              :key="idx"
              class="cpa-details__row"
            >
              <span>{{ rowItem.label }}</span>
              <span>{{ rowItem.req }}</span>
              <span>{{ rowItem.fact }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
    <template v-slot:footer>
      <button
        class="btn btn-outline-primary btn-sm cpa-details__btn-close"
        @click="$emit('update:modelValue', false)"
      >
        {{ $t('offer.offerRequestSuccess.close') }}
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/Modal.vue';
import { useFormatters } from '@/composables/useFormatters';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  states: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const isDefined = (v) => v !== null && v !== undefined;

const store = useStore();
const { t } = useI18n();
const formatters = useFormatters();

const currencyMap = computed(() => {
  const currencies = store.state?.misc?.currencies || [];
  return currencies.reduce((acc, c) => {
    acc[c.code] = c.symbol || `${c.code} `;
    return acc;
  }, {});
});

const getCurrencyLabel = (code) => {
  if (!code) return '';
  return currencyMap.value[code] || `${code} `;
};

const getCond = (curCpa, key) => {
  const node = curCpa?.conditions?.[key] || {};
  const min = (node.min_value !== undefined) ? node.min_value : null;
  const max = (node.max_value !== undefined) ? node.max_value : null;
  const fact = (node.current_value !== undefined) ? node.current_value : null;
  return { min, max, fact };
};

const capPlayers = ({ count, days }) => t('offers.cardOffers.cap_players_per_days', { count, days }, days);

const showCapPlayersRow = (curCpa) => (
  isDefined(curCpa.cap_players_count)
  && isDefined(curCpa.cap_period_seconds)
);

const getCapPlayersText = (curCpa) => {
  const days = Math.round((curCpa.cap_period_seconds || 0) / 86400);
  return capPlayers({ count: curCpa.cap_players_count, days });
};

const buildRowEntries = (curCpa, { key, label, type }) => {
  if (key === 'player_activity') {
    const activity = curCpa.conditions?.player_activity || {};
    if (activity.min_interval === undefined && activity.current_value === undefined) {
      return [];
    }

    return [
      {
        label,
        req: formatters.timeFn(activity.interval_unit, activity.min_interval),
        fact: formatters.timeFn(activity.interval_unit, activity.current_value),
      },
    ];
  }

  const { min, max, fact } = getCond(curCpa, key);
  if (min === null && max === null) return [];

  const fmt = type === 'currency'
    ? (v) => formatters.formatCurrency(v, getCurrencyLabel(curCpa.currency_code))
    : (v) => formatters.formatInteger(v);

  const rows = [];
  if (max !== null) rows.push({ label: `${label} ${t('offers.cardOffers.max')}`, req: fmt(max), fact: fmt(fact) });
  if (min !== null) rows.push({ label: `${label} ${t('offers.cardOffers.min')}`, req: fmt(min), fact: fmt(fact) });
  return rows;
};

const showRootSection = (curCpa) => isDefined(curCpa.amount);

const sectionsTemplate = computed(() => ([
  {
    title: t('offers.cardOffers.deposits'),
    rows: [
      { key: 'deposits_count', label: t('offers.cardOffers.depositsCount'), type: 'int' },
      { key: 'deposits_sum', label: t('offers.cardOffers.depositsSum'), type: 'currency' },
    ],
  },
  {
    title: t('offers.cardOffers.sportBets'),
    rows: [
      { key: 'sport_bets_count', label: t('offers.cardOffers.betsCount'), type: 'int' },
      { key: 'sport_bets_sum', label: t('offers.cardOffers.betsSum'), type: 'currency' },
    ],
  },
  {
    title: t('offers.cardOffers.casinoBets'),
    rows: [
      { key: 'casino_bets_count', label: t('offers.cardOffers.betsCount'), type: 'int' },
      { key: 'casino_bets_sum', label: t('offers.cardOffers.betsSum'), type: 'currency' },
    ],
  },
  {
    title: t('offers.cardOffers.player_activity'),
    rows: [
      { key: 'player_activity', label: t('offers.cardOffers.min_interval'), type: 'time' },
    ],
  },
  {
    title: t('offers.cardOffers.player_registration'),
    rows: [
      { key: 'player_registration', label: t('offers.cardOffers.player_reg'), type: 'int' },
    ],
  },
]));

const getSections = (curCpa) => (
  sectionsTemplate.value.map((section) => {
    const entries = section.rows.flatMap((row) => buildRowEntries(curCpa, row));
    return { title: section.title, entries, visible: entries.length > 0 };
  })
);
</script>

<style lang="scss">
body .modal-body.cpa-details__body {
  max-height: 80vh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1rem 24px !important;
  margin: 0 -24px;
}

body .modal-dialog.cpa-details__dialog {
  max-width: 720px;
  width: 100%;

  .modal-content {
    width: 100%;
  }
}

.cpa-details__btn-close {
  min-width: auto !important;
  padding-left: 30px !important;
  padding-right: 30px !important;
  margin-top: 8px;
}

.cpa-details__title {
  background-color: #f4f7fa;
  padding: 8px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

.cpa-details__row {
  padding: 8px 0;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #e7ecf1;
  &:last-child {
    border-bottom: 0;
  }
}

.cpa-details__title--facts {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr);
  column-gap: 12px;
  align-items: center;
  width: 100%;

  span {
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > span:nth-child(2), > span:nth-child(3) {
    justify-self: end;
    text-align: right;
  }
}

.cpa-details__table--facts {
  .cpa-details__row {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr);
    column-gap: 12px;
    align-items: center;
    width: 100%;
    padding-right: 4px;

    span {
      overflow: hidden;
      min-width: 0;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    > span:nth-child(2), > span:nth-child(3) {
      justify-self: end;
      text-align: right;
    }
  }
}

.cpa-details__section {
  position: relative;
  padding-left: 18px;

  &--root .cpa-details__table {
    margin-left: 2px;
  }

  .offer-card__table-row {
    padding-left: 0 !important;
  }

  .cpa-details__table {
    margin-left: 14px;
    padding-left: 0;
  }

  .cpa-details__title {
    margin-left: 4px;
    position: relative;
    overflow: initial !important;

    &::before {
      content: '';
      position: absolute;
      left: -10px;
      top: 50%;
      height: 1px;
      width: 10px;
      background-color: #eaeaea;
    }
  }

  &:not(:last-child) {
    .cpa-details__table {
      &::after {
        content: '';
        position: absolute;
        left: 12px;
        top: 0;
        height: 100%;
        width: 1px;
        background-color: #eaeaea;
      }
    }

    .cpa-details__title::after {
      content: '';
      position: absolute;
      left: -10px;
      top: 0;
      height: 100%;
      width: 1px;
      background-color: #eaeaea;
    }
  }
  &:last-child {
    .cpa-details__title::after {
      content: '';
      position: absolute;
      left: -10px;
      top: 0;
      height: 50%;
      width: 1px;
      background-color: #eaeaea;
    }
  }
}
</style>
